/**
 * ShareLandingPage
 * Feature: 080-critique-sharing
 *
 * Landing page for share links. When a user receives a share link (via email
 * or direct copy), they land here. The page validates the token and shows
 * options to view the shared content.
 *
 * User Story 4: Email Invite Flow
 *
 * Flow:
 * 1. User clicks share link from email → lands on /share/:token
 * 2. Page validates token → shows collection info
 * 3. User can log in, register, or view as guest
 * 4. On proceed, token is consumed and user is redirected to critique session
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Container, Stack } from '@/components/layout';
import { Heading, Text, Button } from '@/components/atoms';
import {
  useValidateShareTokenGraphQL,
  useConsumeShareTokenGraphQL,
} from '@/graphql/hooks/useSharingGraphQL';
import { useAuth } from '@/services/auth';
import { critiqueUrl } from '@/utils/urls';
import logger from '@/utils/logger';

export function ShareLandingPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const isAuthenticated = !!user;

  // Feature: 892-invite-redirect - read invite token from URL for registration
  const inviteToken = searchParams.get('invite');

  const [validationError, setValidationError] = useState<string | null>(null);

  // Validate the share token
  const {
    data: tokenData,
    isLoading: validating,
    error: validateError,
  } = useValidateShareTokenGraphQL(token);

  // Consume token mutation
  const { consumeToken, isLoading: consuming } = useConsumeShareTokenGraphQL();

  // Track if we've initiated auto-proceed to avoid duplicate calls
  // Using useRef instead of useState to prevent race conditions in strict mode
  const hasInitiatedProceedRef = useRef(false);

  // Check token format on mount
  useEffect(() => {
    if (!token || !/^[0-9a-f]{64}$/i.test(token)) {
      setValidationError('Invalid share link format');
      logger.warn('Sharing', 'Invalid share token format', {
        hasToken: !!token,
        length: token?.length,
      });
    }
  }, [token]);

  // Feature: 892-invite-redirect - Auto-proceed for authenticated users
  // Skip the landing page and go directly to the discussion
  useEffect(() => {
    if (
      isAuthenticated &&
      tokenData?.isValid &&
      tokenData?.critiqueSessionId &&
      !consuming &&
      !hasInitiatedProceedRef.current
    ) {
      hasInitiatedProceedRef.current = true;
      // Consume token and navigate to discussion
      const proceed = async () => {
        try {
          await consumeToken(token!);
          sessionStorage.setItem('shareToken', token!);
        } catch (err) {
          // Continue even if consume fails
          logger.warn('Sharing', 'Failed to consume token during auto-proceed', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
        const url = critiqueUrl(tokenData.critiqueSessionId!, tokenData.collectionSlug || undefined);
        logger.info('Sharing', 'Auto-proceeding to shared content (authenticated user)', {
          critiqueSessionId: tokenData.critiqueSessionId,
        });
        navigate(url);
      };
      proceed();
    }
  }, [isAuthenticated, tokenData, consuming, token, consumeToken, navigate]);

  // Handle proceeding to view the shared content
  // const handleProceed = async () => {
  //   if (!token || !tokenData?.critiqueSessionId) {
  //     logger.error('Sharing', 'Cannot proceed - missing token or session');
  //     return;
  //   }

  //   try {
  //     // Consume the share token (increment use count)
  //     await consumeToken(token);

  //     // Store the token in session storage for context preservation
  //     // This allows the critique session to know how the user arrived
  //     sessionStorage.setItem('shareToken', token);

  //     // Navigate to the critique session
  //     const url = critiqueUrl(tokenData.critiqueSessionId, tokenData.collectionSlug || undefined);
  //     logger.info('Sharing', 'Proceeding to shared content', {
  //       critiqueSessionId: tokenData.critiqueSessionId,
  //       collectionSlug: tokenData.collectionSlug,
  //     });
  //     navigate(url);
  //   } catch (err) {
  //     logger.error('Sharing', 'Failed to consume share token', {
  //       error: err instanceof Error ? err.message : String(err),
  //     });
  //     // Still proceed even if consume fails - we don't want to block access
  //     const url = critiqueUrl(tokenData.critiqueSessionId, tokenData.collectionSlug || undefined);
  //     navigate(url);
  //   }
  // };

  // Loading state
  if (validating || authLoading) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 w-48 bg-muted rounded mx-auto mb-4" />
              <div className="h-4 w-64 bg-muted rounded mx-auto" />
            </div>
            <Text className="mt-4 text-content-secondary">
              Validating share link...
            </Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Invalid token format
  if (validationError) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Invalid Share Link</Heading>
            <Text className="mt-4 text-destructive">{validationError}</Text>
            <Text className="mt-2 text-content-secondary">
              This link appears to be malformed. Please check the link or request a new one.
            </Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Token validation error (network/server error)
  if (validateError) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Error</Heading>
            <Text className="mt-4 text-destructive">
              Unable to validate the share link. Please try again later.
            </Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Token is invalid (expired, exhausted, not found)
  if (tokenData && !tokenData.isValid) {
    const errorMessage = getErrorMessage(tokenData.errorCode);

    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Link Unavailable</Heading>
            <Text className="mt-4 text-destructive">{errorMessage}</Text>
            <Text className="mt-2 text-content-secondary">
              Please request a new share link from the person who shared with you.
            </Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Token is valid - show the shared content info
  return (
    <Container maxWidth="md">
      <Stack direction="vertical" spacing={8} className="py-12">
        {/* Header */}
        <div className="text-center">
          <Heading level="h1">You've been invited to view</Heading>
        </div>

        {/* Collection info card */}
        <div className="bg-surface-secondary rounded-xl p-6 border border-border">
          <div className="text-center">
            <Heading level="h2" className="text-2xl">
              {tokenData?.collectionTitle || 'Photo Collection'}
            </Heading>
            {tokenData?.ownerUsername && (
              <Text className="mt-2 text-content-secondary">
                Shared by @{tokenData.ownerUsername}
              </Text>
            )}
          </div>
        </div>

        {/* Uses remaining notice */}
        {tokenData && tokenData.usesRemaining <= 1 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
            <Text className="text-amber-600 dark:text-amber-400 text-sm">
              This link can only be used{' '}
              {tokenData.usesRemaining === 1 ? 'one more time' : 'once'} before it expires.
            </Text>
          </div>
        )}

        {/* Action buttons */}
        <Stack direction="vertical" spacing={4}>
          {isAuthenticated ? (
            // User is logged in - auto-proceeding to discussion
            // Feature: 892-invite-redirect - show loading state during auto-redirect
            <div className="text-center py-4">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
              <Text className="text-content-secondary">Redirecting to discussion...</Text>
            </div>
          ) : (
            // User is not logged in - must log in or sign up
            <>
              <div className="flex gap-4">
                {/* Feature: 892-invite-redirect - use returnUrl for redirect after login */}
                <Link to={`/login?returnUrl=${encodeURIComponent(`/share/${token}`)}`} className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Log In
                  </Button>
                </Link>
                {/* Feature: 892-invite-redirect - include invite token for registration with returnUrl */}
                <Link
                  to={inviteToken
                    ? `/register?invite=${inviteToken}&returnUrl=${encodeURIComponent(`/share/${token}`)}`
                    : `/register?returnUrl=${encodeURIComponent(`/share/${token}`)}`
                  }
                  className="flex-1"
                >
                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>

              <Text className="text-center text-sm text-content-secondary mt-2">
                Log in or create an account to view this discussion.
              </Text>
            </>
          )}
        </Stack>

        {/* What is 206mp? */}
        <div className="bg-surface-secondary rounded-lg p-4 text-center">
          <Text className="text-sm text-content-secondary">
            <strong>What is 206mp?</strong> A photo critique platform where
            photographers share their work and receive constructive feedback.
          </Text>
        </div>
      </Stack>
    </Container>
  );
}

/**
 * Get user-friendly error message for token validation errors
 */
function getErrorMessage(errorCode: string | null): string {
  switch (errorCode) {
    case 'EXPIRED':
      return 'This share link has expired.';
    case 'EXHAUSTED':
      return 'This share link has already been used the maximum number of times.';
    case 'INVALID_TOKEN':
      return 'This share link is invalid or has been revoked.';
    default:
      return 'This share link is no longer available.';
  }
}
